/* ============================================================================
 * Joonys Pfand-Sync (Layer A + B)  v3.0 — Native Nested Cart Lines + ATC-Inline + Checkout-Guard
 * ----------------------------------------------------------------------------
 * Drei Mechanismen kombiniert:
 *
 * 1) ATC-Inline-Add (Layer A):
 *    Bei jedem `/cart/add.js` mit Item-Property `_pfand_units` > 0 wird der
 *    Request-Body modifiziert: Pfand-Variant wird im SELBEN Request mit
 *    `parent_id` angefügt → Pfand ist sofort als nested cart line da, kein
 *    Race, keine Sekunde "Pfand fehlt noch".
 *
 * 2) Reconcile-Sync (Layer B):
 *    Bei jeder anderen Cart-Mutation (change/update/remove) sync() pro Parent
 *    Pfand-Quantity nach. Idempotent über `parent_relationship.parent_key`.
 *
 * 3) Checkout-Guard:
 *    Wenn irgendein Parent _pfand_units > 0 hat aber keine matching nested
 *    Pfand-Line da ist (oder Quantity falsch), wird `button[name="checkout"]`
 *    disabled. Verhindert Race-Condition wo User Pfand-frei zur Kasse rennt.
 * ========================================================================== */

(function () {
	'use strict';

	const CFG = window.JoonysPfand || {};
	if (!CFG.enabled) return;
	if (!CFG.pfandVariantId) {
		console.warn('[pfand-sync] kein pfandVariantId in window.JoonysPfand – Sync deaktiviert');
		return;
	}

	const VARIANT_ID = String(CFG.pfandVariantId);
	const VARIANT_ID_NUM = parseInt(VARIANT_ID, 10);
	const PROP_UNITS = '_pfand_units';
	const SECTIONS = ['side-cart', 'cart-count', 'main-cart'];
	const DEBUG = !!CFG.debug;

	// Mapping Tray-Selling-Plan → eigener Pfand-Selling-Plan (0% Rabatt, gleiches Intervall).
	// Quelle: window.JoonysPfand.subscriptionPlanMap = { "<trayPlanId>": "<pfandPlanId>" }.
	// Unbekannt/leer → null → Pfand bleibt Einmalkauf. So bricht ATC NIE, auch wenn
	// die Seal-0%-Gruppe (noch) nicht angelegt ist. Erst wenn Mapping da ist, recurrt Pfand.
	const PLAN_MAP =
		CFG.subscriptionPlanMap && typeof CFG.subscriptionPlanMap === 'object' ? CFG.subscriptionPlanMap : {};
	function mapPfandPlan(parentPlanId) {
		if (!parentPlanId) return null;
		const mapped = PLAN_MAP[String(parentPlanId)];
		return mapped ? String(mapped) : null;
	}

	function log() {
		if (DEBUG) console.log.apply(console, ['[pfand-sync]'].concat([].slice.call(arguments)));
	}

	let syncing = false;
	let syncQueued = false;

	// Anzahl noch unbeendeter Cart-Mutations (fetch + XHR). Solange > 0 ODER
	// `syncing` aktiv → Checkout-Button hart deaktiviert, damit User während
	// einer laufenden Reconcile-Operation nicht in den Checkout flutschen kann.
	let pendingMutations = 0;
	function pushPending() {
		pendingMutations += 1;
		forceCheckoutDisabled(true);
	}
	function popPending() {
		pendingMutations = Math.max(0, pendingMutations - 1);
		// Re-Evaluate erst NACHDEM auch der trailing sync gelaufen ist —
		// updateCheckoutGuard wird am Ende von sync() ohnehin aufgerufen.
		if (pendingMutations === 0 && !syncing) {
			updateCheckoutGuard();
		}
	}
	function forceCheckoutDisabled(disabled) {
		document.querySelectorAll('button[name="checkout"]').forEach((btn) => {
			if (disabled) {
				btn.disabled = true;
				btn.classList.add('pfand-pending');
				btn.setAttribute('aria-disabled', 'true');
			}
		});
	}

	// --------------------------------------------------------------------------
	// Helpers
	// --------------------------------------------------------------------------
	function isPfandLine(item) {
		return String(item.variant_id) === VARIANT_ID;
	}

	function getPfandUnits(item) {
		const v = (item.properties || {})[PROP_UNITS];
		const n = parseInt(v || '0', 10);
		return isNaN(n) ? 0 : n;
	}

	// --------------------------------------------------------------------------
	// Cart-API
	// --------------------------------------------------------------------------
	function getCart() {
		return fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
	}

	function changeCart(payload) {
		return fetch('/cart/change.js', {
			method: 'POST',
			credentials: 'same-origin',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify(payload),
		}).then((r) => r.json());
	}

	function addCart(items) {
		return fetch('/cart/add.js', {
			method: 'POST',
			credentials: 'same-origin',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify({ items }),
		}).then((r) => r.json());
	}

	function fetchCartSections() {
		return fetch('/cart?sections=' + encodeURIComponent(SECTIONS.join(',')), {
			credentials: 'same-origin',
		}).then((r) => r.json());
	}

	// --------------------------------------------------------------------------
	// DOM-Refresh
	// --------------------------------------------------------------------------
	const SECTION_TARGETS = [
		{ node: '.side-cart .cart-items', section: 'side-cart', selector: '.side-cart .cart-items' },
		{ node: '.side-cart .sub-total', section: 'side-cart', selector: '.side-cart .sub-total' },
		{ node: '.side-cart .side-cart-footer', section: 'side-cart', selector: '.side-cart .side-cart-footer' },
		{ node: '.cart-table', section: 'main-cart', selector: '.cart-table' },
	];

	function refreshDom(sectionsPayload, totalPrice) {
		if (!sectionsPayload) return;

		// Wenn das Theme `btyUpdateHtml` exponiert, nutzen wir DAS — sonst fallen
		// die Quantity-/Remove-Handler nach unserem DOM-Replace ab. Das Theme macht
		// danach IMMER `btyQuantityButton(), btyUpdateProductQuantity(), ...`, um
		// alle Listener neu zu binden.
		if (typeof window.btyUpdateHtml === 'function') {
			try {
				window.btyUpdateHtml(sectionsPayload, SECTION_TARGETS);
			} catch (e) {
				console.warn('[pfand-sync] btyUpdateHtml threw, fallback to replaceWith:', e);
				SECTION_TARGETS.forEach((t) => {
					const html = sectionsPayload[t.section];
					if (!html) return;
					const liveNode = document.querySelector(t.node);
					if (!liveNode) return;
					const parsed = new DOMParser().parseFromString(html, 'text/html');
					const newNode = parsed.querySelector(t.selector);
					if (!newNode) return;
					liveNode.replaceWith(newNode);
				});
			}
		} else {
			SECTION_TARGETS.forEach((t) => {
				const html = sectionsPayload[t.section];
				if (!html) return;
				const liveNode = document.querySelector(t.node);
				if (!liveNode) return;
				const parsed = new DOMParser().parseFromString(html, 'text/html');
				const newNode = parsed.querySelector(t.selector);
				if (!newNode) return;
				liveNode.replaceWith(newNode);
			});
		}

		// Theme-Re-Init-Funktionen aufrufen, damit Quantity-Buttons + Remove-Links
		// + Side-Cart-Tools nach Section-Replace wieder funktionieren. Reihenfolge
		// und Namen entsprechen exakt dem, was theme.js nach jeder Cart-Mutation tut:
		//   btyQuantityButton(), btyUpdateProductQuantity(),
		//   btyUpdateProgressBarCart(total), btySideCartPopupOuter(), btyAgreeToTerms()
		// btyAddToCart() wird intern bereits von btyUpdateHtml getriggert, im Fallback
		// rufen wir es zusätzlich auf.
		const reinitFns = [
			'btyAddToCart',
			'btyQuantityButton',
			'btyUpdateProductQuantity',
			'btySideCartPopupOuter',
			'btyAgreeToTerms',
		];
		reinitFns.forEach((fn) => {
			try {
				if (typeof window[fn] === 'function') window[fn]();
			} catch (e) {
				console.warn('[pfand-sync] re-init', fn, 'failed:', e);
			}
		});
		if (typeof totalPrice === 'number' && typeof window.btyUpdateProgressBarCart === 'function') {
			try {
				window.btyUpdateProgressBarCart(totalPrice);
			} catch (e) {
				console.warn('[pfand-sync] btyUpdateProgressBarCart failed:', e);
			}
		}
		if (sectionsPayload['cart-count']) {
			const wrap = document.createElement('div');
			wrap.innerHTML = sectionsPayload['cart-count'].trim();
			const raw = (wrap.textContent || '').trim();
			// Sanitize: nur Zahlen erlauben, sonst leer (verhindert NaN-Anzeige)
			const txt = /^\d+$/.test(raw) ? raw : '';
			document.querySelectorAll('.cart-item-count').forEach((el) => {
				el.textContent = txt;
			});
			const sec = document.getElementById('shopify-section-cart-count');
			if (sec) sec.innerHTML = wrap.innerHTML;
		}
		// Final-Sanitizer: jegliche cart-item-count-Bubble mit NaN/undefined wird geleert
		document.querySelectorAll('.cart-item-count').forEach((el) => {
			const t = (el.textContent || '').trim();
			if (t === 'NaN' || t === 'undefined' || t === 'null') el.textContent = '';
		});
	}

	// --------------------------------------------------------------------------
	// Body-Parser für ATC-Inline-Modify
	// --------------------------------------------------------------------------
	function bodyToObj(body) {
		if (!body) return null;
		if (typeof body === 'string') {
			const trimmed = body.trim();
			if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
				try {
					return { kind: 'json', obj: JSON.parse(body) };
				} catch (e) {}
			}
			const params = new URLSearchParams(body);
			const obj = {};
			params.forEach((v, k) => {
				obj[k] = v;
			});
			return { kind: 'form', obj };
		}
		if (body instanceof FormData) {
			const obj = {};
			body.forEach((v, k) => {
				obj[k] = v;
			});
			return { kind: 'form', obj };
		}
		if (body instanceof URLSearchParams) {
			const obj = {};
			body.forEach((v, k) => {
				obj[k] = v;
			});
			return { kind: 'form', obj };
		}
		return null;
	}

	function flatToItem(flat) {
		const item = { id: flat.id, quantity: parseInt(flat.quantity || '1', 10), properties: {} };
		// Abo/Selling-Plan (Seal Subscriptions) durchreichen, sonst geht beim
		// Pfand-Rebuild das Abonnement verloren → Artikel landet als Einmalkauf.
		if (flat.selling_plan) item.selling_plan = flat.selling_plan;
		Object.keys(flat).forEach((k) => {
			const m = k.match(/^properties\[(.+)\]$/);
			if (m) item.properties[m[1]] = flat[k];
		});
		return item;
	}

	function extractItems(parsed) {
		if (!parsed) return null;
		if (parsed.kind === 'json') {
			if (Array.isArray(parsed.obj.items)) return parsed.obj.items;
			if (parsed.obj.id) return [parsed.obj];
			return null;
		}
		if (parsed.obj && parsed.obj.id) return [flatToItem(parsed.obj)];
		return null;
	}

	function enrichItemsWithPfand(items) {
		const out = [];
		items.forEach((item) => {
			out.push(item);
			if (String(item.id) === VARIANT_ID) return; // Pfand selbst nicht erweitern
			const units = getPfandUnits(item);
			if (units <= 0) return;
			const pfand = {
				id: VARIANT_ID_NUM,
				quantity: (parseInt(item.quantity, 10) || 1) * units,
				parent_id: parseInt(item.id, 10),
			};
			// Pfand recurrt im Abo mit: gemappter 0%-Plan, sonst erbt es direkt den
			// Tray-Plan (Pfand muss dafür in Seal in derselben Selling-Plan-Gruppe sein).
			const pfandPlan = mapPfandPlan(item.selling_plan) || item.selling_plan;
			if (pfandPlan) pfand.selling_plan = pfandPlan;
			out.push(pfand);
		});
		return out;
	}

	// --------------------------------------------------------------------------
	// Sync (Layer B): Reconcile nach Cart-Mutationen
	// --------------------------------------------------------------------------
	// Selling-Plan einer Cart-Line lesen (cart.js liefert ihn als selling_plan_allocation).
	function lineSellingPlan(item) {
		return (
			(item.selling_plan_allocation &&
				item.selling_plan_allocation.selling_plan &&
				item.selling_plan_allocation.selling_plan.id) ||
			null
		);
	}

	function computeTargetsByParentKey(cart) {
		const map = new Map();
		cart.items.forEach((item) => {
			if (isPfandLine(item)) return;
			const units = getPfandUnits(item);
			if (units <= 0) return;
			// Pfand recurrt im Abo: gemappter 0%-Plan, sonst direkt der Tray-Plan.
			const parentPlan = lineSellingPlan(item);
			map.set(item.key, {
				quantity: item.quantity * units,
				selling_plan: mapPfandPlan(parentPlan) || parentPlan,
			});
		});
		return map;
	}

	function findPfandLinesByParentKey(cart) {
		const map = new Map();
		cart.items.forEach((item) => {
			if (!isPfandLine(item)) return;
			const rel = item.parent_relationship;
			if (rel && rel.parent_key) map.set(rel.parent_key, item);
		});
		return map;
	}

	async function sync() {
		if (syncing) {
			syncQueued = true;
			return;
		}
		syncing = true;
		try {
			const cart = await getCart();
			const targets = computeTargetsByParentKey(cart);
			const existing = findPfandLinesByParentKey(cart);

			log('targets', Array.from(targets.entries()));
			log('existing', Array.from(existing.keys()));
			// DIAGNOSE: hält Shopify den selling_plan an der (nested) Pfand-Line?
			cart.items.filter(isPfandLine).forEach((it) => {
				log('PFAND-LINE', it.key, '| selling_plan:', lineSellingPlan(it), '| parent:',
					it.parent_relationship && it.parent_relationship.parent_key);
			});

			async function addPfandLine(parentKey, quantity, sellingPlan) {
				const item = { id: VARIANT_ID_NUM, quantity: quantity, parent_line_key: parentKey };
				if (sellingPlan) item.selling_plan = sellingPlan;
				return fetch('/cart/add.js', {
					method: 'POST',
					credentials: 'same-origin',
					headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
					body: JSON.stringify({ items: [item] }),
				}).then((r) => r.json());
			}

			let didMutate = false;
			for (const [parentKey, target] of targets) {
				const exist = existing.get(parentKey);
				if (exist) {
					// selling_plan lässt sich nicht per /cart/change ändern (z.B. Einmal→Abo
					// am Parent) → Pfand-Line entfernen und mit korrektem Plan neu anlegen.
					const planMismatch = (lineSellingPlan(exist) || null) !== (target.selling_plan || null);
					if (planMismatch) {
						log('replan', parentKey, lineSellingPlan(exist), '→', target.selling_plan);
						await changeCart({ id: exist.key, quantity: 0 });
						await addPfandLine(parentKey, target.quantity, target.selling_plan);
						didMutate = true;
					} else if (exist.quantity !== target.quantity) {
						log('change', parentKey, exist.quantity, '→', target.quantity);
						await changeCart({ id: exist.key, quantity: target.quantity });
						didMutate = true;
					}
					existing.delete(parentKey);
				} else {
					log('add', parentKey, target.quantity, 'plan', target.selling_plan);
					await addPfandLine(parentKey, target.quantity, target.selling_plan);
					didMutate = true;
				}
			}
			for (const [parentKey, item] of existing) {
				log('remove orphan pfand', parentKey);
				await changeCart({ id: item.key, quantity: 0 });
				didMutate = true;
			}

			if (didMutate) {
				const sections = await fetchCartSections();
				const freshCart = await getCart();
				refreshDom(sections, freshCart && typeof freshCart.total_price === 'number' ? freshCart.total_price : undefined);
			}

			document.dispatchEvent(new CustomEvent('pfand:synced', { detail: { didMutate } }));
		} catch (err) {
			console.error('[pfand-sync] sync failed:', err);
		} finally {
			syncing = false;
			// Guard NACH dem Zurücksetzen von `syncing` ausführen, sonst meldet er
			// fälschlicherweise BUSY und der Button bleibt gesperrt.
			updateCheckoutGuard().catch((e) => console.warn('[pfand-sync] guard after sync failed:', e));
			if (syncQueued) {
				syncQueued = false;
				setTimeout(sync, 50);
			}
		}
	}

	// --------------------------------------------------------------------------
	// Checkout-Guard
	// --------------------------------------------------------------------------
	async function updateCheckoutGuard(cartIn) {
		// Busy: laufende Cart-Mutation oder laufender Sync → IMMER gesperrt.
		const busy = pendingMutations > 0 || syncing;
		const cart = cartIn || (await getCart());
		const targets = computeTargetsByParentKey(cart);
		const existing = findPfandLinesByParentKey(cart);
		let mismatch = false;
		for (const [parentKey, target] of targets) {
			const exist = existing.get(parentKey);
			if (!exist || exist.quantity !== target.quantity) {
				mismatch = true;
				break;
			}
		}
		const blocked = busy || mismatch;
		document.querySelectorAll('button[name="checkout"]').forEach((btn) => {
			btn.disabled = blocked;
			btn.classList.toggle('pfand-pending', blocked);
			if (blocked) btn.setAttribute('aria-disabled', 'true');
			else btn.removeAttribute('aria-disabled');
		});
		log('checkout-guard', blocked ? (busy ? 'BUSY' : 'MISMATCH') : 'OK');
		return blocked;
	}

	// --------------------------------------------------------------------------
	// Fetch-Interceptor (1) ATC-Inline-Modify, (2) Trigger Sync
	// --------------------------------------------------------------------------
	const CART_MUTATION_PATHS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];

	function urlOf(input) {
		return typeof input === 'string' ? input : input && input.url ? input.url : '';
	}

	function isCartAdd(url) {
		return /\/cart\/add(\.js|\?|$)/.test(url);
	}
	function isCartMutation(url) {
		return CART_MUTATION_PATHS.some((p) => url.indexOf(p) !== -1);
	}

	const _fetch = window.fetch;
	window.fetch = function (input, init) {
		const url = urlOf(input);
		let effectiveInput = input;
		let effectiveInit = init;

		// (1) ATC-Inline: bei /cart/add.js mit _pfand_units → Pfand mitsenden
		if (!syncing && isCartAdd(url) && init && init.body) {
			const parsed = bodyToObj(init.body);
			const items = extractItems(parsed);
			if (items && items.length) {
				const enriched = enrichItemsWithPfand(items);
				if (enriched.length > items.length) {
					log('atc-inline: enriching', items.length, '→', enriched.length, 'items', enriched);
					// Sections direkt mitanfordern, damit der Response-Refresh sofort
					// die Pfand-Line im Drawer rendert (kein Re-Sync nötig).
					if (typeof effectiveInput === 'string' && effectiveInput.indexOf('sections=') === -1) {
						const sep = effectiveInput.indexOf('?') === -1 ? '?' : '&';
						effectiveInput = effectiveInput + sep + 'sections=' + encodeURIComponent(SECTIONS.join(','));
					}
					const newBody = JSON.stringify({ items: enriched });
					effectiveInit = Object.assign({}, init, {
						body: newBody,
						headers: Object.assign({}, init.headers || {}, {
							'Content-Type': 'application/json',
							Accept: 'application/json',
						}),
					});
				}
			}
		}

		// Bei Cart-Mutation: sofort Checkout-Guard scharf schalten (vor dem
		// eigentlichen Fetch), damit kein Race zwischen Klick und Sync möglich ist.
		const isMut = isCartMutation(url);
		if (isMut) pushPending();

		const promise = _fetch.call(this, effectiveInput, effectiveInit);

		// (2) Bei jeder Cart-Mutation: Reconcile-Sync nachträglich
		if (!isMut) return promise;
		return promise
			.then(async (res) => {
				// Wenn der Response Sections enthält → sofort refreshen (Pfand sichtbar machen)
				if (isCartAdd(url)) {
					try {
						const cloned = res.clone();
						const data = await cloned.json();
						if (data && data.sections) {
							// Response von /cart/add.js enthält das hinzugefügte Item,
							// NICHT den Cart-Total. Total separat holen für btyUpdateProgressBarCart.
							let total;
							try {
								const fresh = await getCart();
								if (fresh && typeof fresh.total_price === 'number') total = fresh.total_price;
							} catch (_) {}
							refreshDom(data.sections, total);
						}
					} catch (e) {
						/* ignore */
					}
				}
				return res;
			})
			.then(
				(res) => {
					popPending();
					if (!syncing) setTimeout(sync, 60);
					return res;
				},
				(err) => {
					popPending();
					throw err;
				}
			);
	};

	// --------------------------------------------------------------------------
	// XMLHttpRequest-Interceptor (nur Sync-Trigger, kein Inline-Modify)
	// --------------------------------------------------------------------------
	const _xhrOpen = XMLHttpRequest.prototype.open;
	const _xhrSend = XMLHttpRequest.prototype.send;
	XMLHttpRequest.prototype.open = function (method, url) {
		this.__pfandUrl = url;
		return _xhrOpen.apply(this, arguments);
	};
	XMLHttpRequest.prototype.send = function () {
		const url = this.__pfandUrl || '';
		if (isCartMutation(url)) {
			pushPending();
			let popped = false;
			const popOnce = function () {
				if (popped) return;
				popped = true;
				popPending();
			};
			// loadend feuert auch bei abort/error — guarded popOnce stellt sicher,
			// dass der Counter sich nicht stapelt.
			this.addEventListener('loadend', function () {
				popOnce();
				if (!syncing) setTimeout(sync, 60);
			});
			this.addEventListener('error', popOnce);
			this.addEventListener('abort', popOnce);
		}
		return _xhrSend.apply(this, arguments);
	};

	// --------------------------------------------------------------------------
	// NaN-Sanitizer: MutationObserver auf alle cart-item-count Bubbles
	// (Theme-Code rechnet teilweise mit text und schreibt NaN — wir killen das sofort)
	// --------------------------------------------------------------------------
	function sanitizeBubble(el) {
		const t = (el.textContent || '').trim();
		if (t === '' || t === '0') {
			if (el.textContent !== '') el.textContent = '';
			return;
		}
		if (!/^\d+$/.test(t)) {
			el.textContent = '';
		}
	}

	function attachBubbleSanitizer() {
		document.querySelectorAll('.cart-item-count').forEach((el) => {
			sanitizeBubble(el);
			if (el.__pfandSanitized) return;
			el.__pfandSanitized = true;
			const obs = new MutationObserver(() => sanitizeBubble(el));
			obs.observe(el, { childList: true, characterData: true, subtree: true });
		});
	}

	// --------------------------------------------------------------------------
	// Initial-State
	// --------------------------------------------------------------------------
	function init() {
		attachBubbleSanitizer();
		updateCheckoutGuard();
		setTimeout(sync, 200);
		// Bei jedem Sync: Bubble-Sanitizer neu attachen (DOM könnte ersetzt sein)
		document.addEventListener('pfand:synced', attachBubbleSanitizer);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}

	window.JoonysPfandSync = { sync, updateCheckoutGuard, version: '3.2.0' };
})();
